/**
 * Player — the heart of "feel". Consumes a PhysicsConfig (via the active form)
 * and implements the full model the brief asks for:
 *   - walk/run dual top speed, asymmetric accel/decel, ground vs air control
 *   - turn-around SKID state (input opposes velocity over a threshold)
 *   - variable jump height (early release → heavier gravity while rising)
 *   - coyote time + jump buffering
 *   - terminal velocity
 *   - optional speed→jump scaling hook
 * Physics body (AABB) is decoupled from the sprite frame; rendering lives in World.
 */
import type { ResolvedForm } from './forms';
import type { InputSnapshot } from './Input';
import type { PhysicsConfig } from './PhysicsConfig';
import { type AABB, TileGrid } from './TileGrid';

export type PlayerState = 'idle' | 'walk' | 'run' | 'jump' | 'fall' | 'skid' | 'duck';

export interface PlayerEvents {
  onJump?: () => void;
  /** @param impactVy downward speed (px/s, +down) at the instant of landing, for scaling a
   *  landing squash. Render-only consumers; never feed it back into the sim. */
  onLand?: (impactVy: number) => void;
  onBump?: () => void;
  /**
   * RUN-AND-GUN: the player fired a bullet this step. `dirX`/`dirY` is the NORMALIZED aim
   * unit vector (8-way, resolved from axisX + aimUp/duck). The Player has no entity access, so
   * World/Sim wires this to spawnEntityAt (mirrors how onJump plays a sound). Off for every
   * other archetype (the shoot verb gates the fire path).
   */
  onShoot?: (dirX: number, dirY: number) => void;
  /**
   * POLISH_01 #4: a dash was ARMED this step. `dir` is the committed horizontal sign (+1/-1).
   * Cosmetic (whoosh + trauma + trailing dust in World); carries no sim state → replay-safe.
   */
  onDash?: (dir: number) => void;
  /**
   * POLISH_01 #8: the player crossed the false→true edge of the SKID (turn-around brake) state.
   * Cosmetic (screech + dust). Fired once per skid onset.
   */
  onSkid?: () => void;
}

export class Player {
  body: AABB = { x: 0, y: 0, w: 44, h: 84 };
  vx = 0;
  vy = 0;
  grounded = false;
  facing: 1 | -1 = 1;
  ducking = false;
  skidding = false;
  running = false;
  state: PlayerState = 'idle';

  // ── PRECISION wall verbs (Archetype) ────────────────────────────────────────
  // World sets these from the active archetype. OFF by default → CLASSIC never enters
  // the wall code path and stays bit-identical. wallSliding is the SIM-STATE boolean
  // the render/UI can reflect later (the handoff's required state surface).
  wallSlideEnabled = false;
  wallJumpEnabled = false;
  /** True this step while in the passive wall-slide state (airborne, beside a wall). */
  wallSliding = false;
  /** Side the clung wall is on this step (-1 left, +1 right, 0 none) — for render/debug. */
  wallSide: -1 | 0 | 1 = 0;
  /** Fixed steps left of the post-wall-jump control lock (input suppressed so the kick
   *  carries the player off the wall). Integer → deterministic across a replay. */
  private wallJumpLockSteps = 0;

  // ── COMBAT verbs (Archetype) ─────────────────────────────────────────────────
  // World sets these from the active archetype. OFF by default → a non-COMBAT form never
  // arms a hitbox and stays bit-identical. The counters are integer step counters
  // (deterministic across a replay); attackDir is the live strike aim (also the dash
  // hitbox direction). The pogo/dash CONSEQUENCE is applied by World via onAttackConnect.
  attackEnabled = false;
  dashEnabled = false;
  /** Current strike aim, or null when no strike is live. 'up' reserved for a later option. */
  attackDir: 'left' | 'right' | 'up' | 'down' | null = null;
  /** Fixed steps the sword hitbox stays live (counts down each update). */
  attackActiveSteps = 0;
  /** Fixed steps until `attack` can fire again. */
  attackCooldownSteps = 0;
  /** Fixed steps the dash-kill lunge stays live. */
  dashActiveSteps = 0;
  /** Fixed steps until `dash` can fire again (a connect refreshes this to 0; COMBAT cooldown model). */
  dashCooldownSteps = 0;
  /** The committed dash HORIZONTAL sign while dashActiveSteps > 0 (facing + render streak). */
  dashDir: 1 | -1 = 1;
  /** The committed dash velocity (px/s) while the lunge is live — set at the dash press, reused
   *  each step. For COMBAT this is always (dashDir*dashVelocity, 0) → byte-identical to the old
   *  horizontal-only lunge; DASH-PRECISION's 8-way aim fills in a vertical component. */
  dashVx = 0;
  dashVy = 0;

  // ── DASH-PRECISION verbs (Archetype) ─────────────────────────────────────────
  // World sets climbEnabled from the archetype. The charge model activates when
  // config.dashMaxCharges > 0 (DASH-PRECISION); COMBAT keeps dashMaxCharges = 0 → its dash
  // stays on the cooldown model, byte-identical. All counters/scalars are integer (or exact)
  // → deterministic across a replay.
  /** Stamina wall-climb verb enabled (DASH-PRECISION). OFF elsewhere → climb code never runs. */
  climbEnabled = false;
  /** Remaining climb stamina (abstract integer units; drains while gripping, refills on ground). */
  climbStamina = 0;
  /** True this step while actively climbing a wall (for render/HUD). */
  climbing = false;
  /** Remaining air-dash charges (charge model — DASH-PRECISION). Consumed on dash, refilled on
   *  land (and by a dashRefill pickup). Unused by COMBAT (dashMaxCharges = 0). */
  dashCharges = 0;
  /** Fixed steps left of the DASH-FREEZE hitstop (DASH-PRECISION / Celeste): while > 0 the
   *  player is frozen in place before the lunge integrates. Set only by the charge-model dash
   *  (config.dashFreezeSteps), so COMBAT's cooldown dash never freezes → byte-identical.
   *  Integer step counter → deterministic across a replay. */
  private dashFreezeCounter = 0;

  // ── RUN-AND-GUN verb (Archetype) ──────────────────────────────────────────────
  // World/Sim sets shootEnabled from the archetype. OFF by default → a non-RUN-AND-GUN form
  // never reads the fire input and stays byte-identical. shootCooldownSteps is an integer step
  // counter (the held-auto-fire rate gate), ticked down each step and reset in placeFeet →
  // deterministic across a replay.
  /** RUN-AND-GUN fire verb enabled. OFF elsewhere → the fire path never runs. */
  shootEnabled = false;
  /** Fixed steps until the gun can fire again (the auto-fire spray-rate cap). */
  shootCooldownSteps = 0;

  // ── AUTO-RUNNER verb (Archetype) ──────────────────────────────────────────────
  // Active only when config.autoRunSpeed > 0 (set via the archetype profile). The live run
  // direction: horizontal cruise is autoRunDir * autoRunSpeed and a wall-jump FLIPS it (the
  // turn mechanic). Defaults +1 (right — the confirmed start dir, §5.4) and is reset in
  // placeFeet. Never read when autoRunSpeed is 0 → every other archetype is byte-identical.
  autoRunDir: 1 | -1 = 1;

  private timeSinceGroundedMs = 9999;
  private jumpBufferTimerMs = 0;
  private walkPhase = 0;
  /** |vx| at the instant the player last SLAMMED into a wall (captured before collideX zeroes
   *  vx). The MOMENTUM additive wall-jump reflects THIS — otherwise the collision discards the
   *  speed before the next step's kick can use it, and alternating kicks never build. Written
   *  on every horizontal collision (a no-op for non-additive archetypes, which never read it →
   *  bit-identical); cleared on the kick + on landing. */
  private wallHitSpeed = 0;
  /** Last step's `skidding` value — for the false→true skid-onset edge (POLISH_01 #8). Render-only
   *  signal; never affects physics. Reset on (re)spawn + on the dash/freeze early-outs. */
  private prevSkidding = false;

  constructor(public form: ResolvedForm) {
    this.body.w = form.body.width;
    this.body.h = form.body.height;
  }

  get config() { return this.form.config; }

  /** Place feet-bottom-center at a world point (used at spawn). */
  placeFeet(cx: number, feetY: number): void {
    this.body.x = cx - this.body.w / 2;
    this.body.y = feetY - this.body.h;
    this.vx = 0; this.vy = 0; this.grounded = false;
    this.timeSinceGroundedMs = 9999; this.jumpBufferTimerMs = 0;
    this.wallJumpLockSteps = 0; this.wallSliding = false; this.wallSide = 0;
    // COMBAT: clear any live/queued strike so a respawn never carries a hitbox or cooldown.
    this.attackDir = null;
    this.attackActiveSteps = 0; this.attackCooldownSteps = 0;
    this.dashActiveSteps = 0; this.dashCooldownSteps = 0;
    this.dashVx = 0; this.dashVy = 0;
    this.dashFreezeCounter = 0;
    // RUN-AND-GUN: clear the fire cooldown so a respawn never carries a stale rate gate.
    this.shootCooldownSteps = 0;
    // DASH-PRECISION: spawn with a full charge + full stamina, no live climb. Reads the active
    // form's config (inert 0 for non-DASH-PRECISION → these stay 0, byte-identical).
    this.dashCharges = this.config.dashMaxCharges;
    this.climbStamina = this.config.climbStaminaMax;
    this.climbing = false;
    this.wallHitSpeed = 0;
    // AUTO-RUNNER: (re)spawn cruising RIGHT (the confirmed start direction, §5.4). No-op for
    // every other archetype (autoRunDir is never read when autoRunSpeed is 0).
    this.autoRunDir = 1;
    this.prevSkidding = false; // render-only edge tracker; a respawn never carries a stale skid
  }

  /**
   * POLISH_03 #9: reposition for a WARP/DOOR teleport — moves the body + zeroes velocity/ground
   * state ONLY (the position-coupled transients that would be meaningless at the new location),
   * WITHOUT placeFeet's full per-life reset. Unlike placeFeet this deliberately preserves
   * dashCharges/climbStamina/autoRunDir/every cooldown, so a warp never hands out a free
   * dash/stamina refill, never drops hard-won momentum other than what a teleport itself implies,
   * and never forces an AUTO-RUNNER to suddenly cruise right through a left-running corridor.
   */
  teleportFeet(cx: number, feetY: number): void {
    this.body.x = cx - this.body.w / 2;
    this.body.y = feetY - this.body.h;
    this.vx = 0; this.vy = 0; this.grounded = false;
    this.timeSinceGroundedMs = 9999; this.jumpBufferTimerMs = 0;
    this.wallJumpLockSteps = 0; this.wallSliding = false; this.wallSide = 0;
    // Clear any LIVE strike/dash window (its hitbox geometry was relative to the old position) —
    // but leave attackCooldownSteps/dashCooldownSteps/dashCharges/climbStamina/autoRunDir untouched.
    this.attackDir = null;
    this.attackActiveSteps = 0;
    this.dashActiveSteps = 0;
    this.dashVx = 0; this.dashVy = 0;
    this.dashFreezeCounter = 0;
    this.climbing = false;
    this.wallHitSpeed = 0;
    this.prevSkidding = false;
  }

  /** Swap form (physics + avatar). Keeps feet anchored so growth pushes the head up. */
  setForm(form: ResolvedForm): void {
    const feetY = this.body.y + this.body.h;
    const cx = this.body.x + this.body.w / 2;
    this.form = form;
    this.body.w = form.body.width;
    this.body.h = form.body.height;
    this.body.x = cx - this.body.w / 2;
    this.body.y = feetY - this.body.h;
  }

  update(dt: number, input: InputSnapshot, grid: TileGrid, ev: PlayerEvents): void {
    const c = this.config;
    const dtMs = dt * 1000;
    const groundedNow = this.grounded; // state from last step

    // Decrement the post-wall-jump control lock (integer step → deterministic).
    if (this.wallJumpLockSteps > 0) this.wallJumpLockSteps--;

    // ── COMBAT verbs (gated; inert/no-op for non-COMBAT) ──────────────────────────
    // Tick the attack/dash counters, then arm a strike on this step's rising edge.
    this.tickCombat();
    this.handleCombatInput(input, groundedNow, ev);

    // ── RUN-AND-GUN fire verb (gated; no-op for non-RUN-AND-GUN) ──────────────────
    // Tick the fire cooldown, then emit a bullet while FIRE is held + the cooldown has elapsed
    // (held auto-fire). Placed here (above the COMBAT dash early-returns) so it runs every step
    // for RUN-AND-GUN; those early-return branches never fire for it (dash/attack verbs off).
    this.tickShoot();
    this.handleShootInput(input, ev);

    // DASH-FREEZE (DASH-PRECISION / Celeste): hold the player frozen in place for a few
    // steps at the start of an 8-way charge dash — the committal hitstop "punch". Set only
    // by the charge-model dash, so COMBAT (cooldown model, dashFreezeSteps 0) never enters
    // here → byte-identical. While frozen we don't integrate and tickCombat holds the lunge
    // window (gated on the counter), so the full lunge runs AFTER the freeze.
    if (this.dashFreezeCounter > 0) {
      this.dashFreezeCounter--;
      this.vx = 0; this.vy = 0;
      this.skidding = false; this.ducking = false; this.climbing = false;
      this.wallSliding = false; this.wallSide = 0;
      this.prevSkidding = false; // re-arm the skid edge for after the freeze
      this.state = this.computeState(input.runActive);
      return;
    }

    // DASH-KILL: a committed flat horizontal lunge handled as an early branch — it
    // overrides the normal accel / jump / gravity for its window, then does a
    // shared-style move+collide so the swept body lets the attack hitbox (extended in
    // dashDir) hit enemies along the path. A wall ends the dash. Only COMBAT reaches
    // here (dashActiveSteps stays 0 otherwise → byte-identical for other archetypes).
    if (this.dashActiveSteps > 0) {
      this.facing = this.dashDir;
      this.running = input.runActive;
      this.ducking = false; this.skidding = false; this.climbing = false;
      this.wallSliding = false; this.wallSide = 0;
      this.vx = this.dashVx;
      this.vy = this.dashVy; // 0 for COMBAT's flat lunge → byte-identical; DASH-PRECISION 8-way fills it
      if (grid.collideX(this.body, this.vx * dt)) { this.vx = 0; this.dashActiveSteps = 0; }
      const dres = grid.collideY(this.body, this.vy * dt, false);
      // A vertical/diagonal dash (DASH-PRECISION) ends on a floor/ceiling hit too; COMBAT's flat
      // lunge has dashVy=0 so this guard never fires for it → byte-identical.
      if (this.vy !== 0 && (dres.grounded || dres.ceiling)) { this.vy = 0; this.dashActiveSteps = 0; }
      if (dres.grounded) { this.grounded = true; this.timeSinceGroundedMs = 0; this.refillOnGround(c); }
      else { this.grounded = false; this.timeSinceGroundedMs += dtMs; }
      this.walkPhase = 0;
      this.prevSkidding = false; // re-arm the skid edge for after the dash lunge
      this.state = this.computeState(this.running);
      return;
    }

    // ── Horizontal model ──────────────────────────────────────────────────────
    // AUTO-RUNNER (§3): when autoRunSpeed > 0 the horizontal model is AUTOMATIC — the run
    // direction (autoRunDir, flipped by a wall-jump in §4) drives a constant cruise and axisX is
    // deterministically IGNORED. `intent` is the sign that feeds skid / overspeed / wall-detect:
    // the run dir under auto-run (the player never "opposes" their own cruise unless a wall-jump
    // flipped it — the intended turn), else axisX. autoRunSpeed 0 collapses every gate below to
    // the legacy axisX expression, so CLASSIC / every other archetype is byte-identical.
    const autoRun = c.autoRunSpeed > 0;
    const ax = input.axisX;
    const running = input.runActive;
    this.running = running;
    this.ducking = input.duck && groundedNow;
    const intent = autoRun ? this.autoRunDir : ax;

    // Wall adjacency (Archetype): a pure positional probe. CLASSIC skips it (both verb flags off
    // → detectWall returns 0). Drives the wall-slide state + the wall-jump launch direction. Under
    // auto-run the intent (run dir) picks which wall wins in a two-wall shaft.
    this.wallSide = this.detectWall(intent, groundedNow, grid);
    this.wallSliding = this.wallSlideEnabled && !groundedNow && this.wallSide !== 0;

    // CLIMB (DASH-PRECISION): grip a wall with stamina to ASCEND. Gated on the climb verb + the
    // held `grab` + airborne + beside a wall + stamina remaining. Drains stamina each step; at 0
    // the grip fails and the player drops into the wall-slide. Inert for every other archetype
    // (climbEnabled false). Integer drain → deterministic across a replay.
    this.climbing = this.climbEnabled && input.grab && !groundedNow
      && this.wallSide !== 0 && this.climbStamina > 0;
    if (this.climbing) {
      this.climbStamina -= c.climbDrainPerStep;
      if (this.climbStamina < 0) this.climbStamina = 0;
      this.wallSliding = false; // an active grip overrides the passive slide
    }

    // Air authority: blend the airborne accel/decel toward the GROUND rates by
    // airControl (0 = legacy independent air model → CLASSIC bit-identical; 1 = full
    // ground authority in the air = near-total mid-flight steering, the PRECISION feel).
    const ac = c.airControl;
    const airAccelEff = ac > 0 ? c.airAccel + (c.groundAccel - c.airAccel) * ac : c.airAccel;
    const airDecelEff = ac > 0 ? c.airDecel + (c.groundDecel - c.airDecel) * ac : c.airDecel;

    const maxSpeed = autoRun ? c.autoRunSpeed : (running ? c.runMaxSpeed : c.walkMaxSpeed);
    // AUTO-RUNNER: cruise at autoRunDir * autoRunSpeed (axisX ignored; a grounded duck does NOT
    // stop the cruise — the dive is the air-only vertical verb). Else the legacy axisX target.
    const target = autoRun ? this.autoRunDir * c.autoRunSpeed : (this.ducking ? 0 : ax * maxSpeed);
    this.skidding = false;
    let rate: number;
    let coasting = false; // true when airborne with no horizontal input → MOMENTUM air-drag path

    if (intent !== 0 && (autoRun || !this.ducking)) {
      this.facing = autoRun ? this.autoRunDir : (ax > 0 ? 1 : -1);
      const opposing = this.vx !== 0 && Math.sign(intent) !== Math.sign(this.vx);
      const overspeed = Math.abs(this.vx) > maxSpeed + 1 && Math.sign(this.vx) === Math.sign(intent);
      if (groundedNow && opposing && Math.abs(this.vx) > c.skidThreshold) {
        this.skidding = true;
        rate = c.skidDecel;
      } else if (!groundedNow && opposing && c.airBrakeDecel > 0 && Math.abs(this.vx) > c.skidThreshold) {
        // Air-brake (handoff §1): an airborne reversal over the threshold uses a DISTINCT
        // air-brake rate (the air mirror of the grounded skid) — MOMENTUM-NINJA's "momentum
        // persists but you can still turn, it costs". airBrakeDecel defaults to 0 → this branch
        // is skipped → the reversal falls to the accel branch exactly as before, so
        // CLASSIC/PRECISION/COMBAT are bit-identical.
        rate = c.airBrakeDecel;
      } else if (overspeed) {
        rate = groundedNow ? c.groundDecel : airDecelEff;
      } else {
        rate = groundedNow ? c.groundAccel : airAccelEff;
      }
    } else {
      // no input (or ducking) → decelerate toward 0
      rate = groundedNow ? c.groundDecel : airDecelEff;
      coasting = true;
    }
    // Wall-jump control lock: suppress horizontal input authority for a few steps so the
    // kick carries the player off the wall before (near-total) air control resumes.
    if (this.wallJumpLockSteps > 0) rate = 0;
    // MOMENTUM multiplicative air-drag: while COASTING in the air (no horizontal input), a
    // per-step `vx *= k` bleed (speed-PROPORTIONAL) REPLACES the flat air-coast decel — the
    // N++ "glide, momentum persists" feel a flat px/s² can't reproduce. Gated on
    // airDragMultiplier > 0 (inert elsewhere → bit-identical), airborne, coasting, and not
    // inside the wall-jump lock (so a fresh kick isn't sapped). A held direction takes the
    // accel/brake path above, so the drag never fights the additive wall-jump build.
    if (coasting && !groundedNow && c.airDragMultiplier > 0 && this.wallJumpLockSteps === 0) {
      this.vx *= c.airDragMultiplier;
    } else {
      this.vx = approach(this.vx, target, rate * dt);
    }

    // ── Jump: buffer + coyote (+ PRECISION wall-jump) ─────────────────────────
    if (input.jumpPressed) this.jumpBufferTimerMs = c.jumpBufferMs;
    this.jumpBufferTimerMs = Math.max(0, this.jumpBufferTimerMs - dtMs);

    const canCoyote = this.timeSinceGroundedMs <= c.coyoteTimeMs;
    if (this.jumpBufferTimerMs > 0 && (this.grounded || canCoyote)) {
      let jv = c.jumpVelocity;
      if (c.speedToJumpScale > 0) {
        jv += c.jumpVelocity * c.speedToJumpScale * Math.min(1, Math.abs(this.vx) / c.runMaxSpeed);
      }
      this.vy = -jv;
      this.grounded = false;
      this.timeSinceGroundedMs = c.coyoteTimeMs + 1; // consume coyote so we can't double-fire
      this.jumpBufferTimerMs = 0;
      ev.onJump?.();
    } else if (this.jumpBufferTimerMs > 0 && this.wallJumpEnabled && (this.wallSliding || this.climbing)) {
      // Wall-jump (PRECISION/DASH-PRECISION/MOMENTUM): launch AWAY from the wall (-wallSide) and
      // up, then lock horizontal control briefly so the kick lands before air control cancels it.
      // Works from a passive wall-slide OR an active climb grip.
      const launch = -this.wallSide; // +1 = launch right, -1 = launch left
      if (c.wallJumpAdditive) {
        // MOMENTUM-NINJA: REFLECT the incoming speed off the wall + ADD the kick, so alternating
        // -wall kicks BUILD speed ("build up a ton of speed", N++). NOTE the handoff's literal
        // `vx += -wallSide*pushX` CANCELS on alternation (the push flips sign vs the incoming
        // direction each kick); reflect-and-add is the generalization that actually makes
        // "momentum survive the wall-jump". Capped so it can't diverge.
        const cap = c.wallJumpMaxSpeedX > 0 ? c.wallJumpMaxSpeedX : c.runMaxSpeed * 2;
        // Reflect the speed the player CARRIED INTO the wall (the slam speed, or the live vx if
        // higher) so alternating-wall kicks accumulate; the collision zeroed vx last step, so
        // without wallHitSpeed there is nothing to reflect.
        const incoming = Math.max(Math.abs(this.vx), this.wallHitSpeed);
        let speed = incoming + c.wallJumpPushX;
        if (speed > cap) speed = cap;
        this.vx = launch * speed;
        this.wallHitSpeed = 0; // consumed
      } else {
        this.vx = launch * c.wallJumpPushX; // PRECISION/DASH-PRECISION: SET (unchanged)
      }
      this.vy = -c.wallJumpVelocityY;
      this.facing = launch < 0 ? -1 : 1; // face the launch direction (off the wall)
      // AUTO-RUNNER (§4): kicking off a wall REVERSES the cruise direction (the turn mechanic).
      // Gated on auto-run → autoRunDir is never written for any other archetype (byte-identical).
      if (autoRun) this.autoRunDir = launch < 0 ? -1 : 1;
      this.grounded = false;
      this.timeSinceGroundedMs = c.coyoteTimeMs + 1;
      this.wallJumpLockSteps = c.wallJumpControlLockSteps;
      this.wallSliding = false;
      this.climbing = false;
      this.jumpBufferTimerMs = 0;
      ev.onJump?.();
    }

    // ── Gravity (variable jump height) ────────────────────────────────────────
    if (this.climbing) {
      // DASH-PRECISION: an active grip ascends at climbSpeed and negates gravity entirely
      // (drop the grip — release `grab`, run out of stamina, or wall-jump — to fall again).
      this.vy = -c.climbSpeed;
    } else {
      const rising = this.vy < 0;
      const releasedEarly = rising && !input.jumpHeld;
      // DIVE (AUTO-RUNNER §5.2): hold duck in the AIR → a strong fast-fall toward diveSpeed (the
      // vertical control for threading gaps). Gated on auto-run + diveSpeed > 0 + airborne, so
      // every other archetype takes the else and is byte-identical. diveSpeed > terminalVelocity,
      // so the dive overrides the normal terminal clamp.
      const diving = autoRun && c.diveSpeed > 0 && input.duck && !groundedNow;
      if (diving) {
        this.vy += c.diveGravity * dt;
        if (this.vy > c.diveSpeed) this.vy = c.diveSpeed;
      } else {
        let g = releasedEarly ? c.jumpReleaseGravity : c.gravity;
        // APEX-HANG (DASH-PRECISION / Celeste): near the top of a HELD jump, swap to a lighter
        // gravity for a brief readable float. Gated on apexHangThreshold > 0 (inert elsewhere →
        // bit-identical) and skipped on an early-release cut (which keeps its heavier release
        // gravity so a tapped jump still feels snappy). Math.abs only → lint-clean.
        if (c.apexHangThreshold > 0 && !releasedEarly && Math.abs(this.vy) < c.apexHangThreshold) {
          g = c.apexHangGravity;
        }
        this.vy += g * dt;
        if (this.vy > c.terminalVelocity) this.vy = c.terminalVelocity;
      }
      // PRECISION wall-slide: clamp the DESCENT to a slow controlled cling while clung to
      // a wall (rising is untouched, so a wall-jump arc is unaffected).
      if (this.wallSliding && this.vy > c.wallSlideMaxFallSpeed) this.vy = c.wallSlideMaxFallSpeed;
    }

    // ── Move + collide (axes separate) ────────────────────────────────────────
    // Capture the slam speed BEFORE zeroing so the MOMENTUM additive kick can reflect it next
    // step (write-only here; non-additive archetypes never read it → byte-identical). Keep the
    // MAX until the kick consumes it: a player pressing INTO a wall re-collides at ~0 every step,
    // which would otherwise overwrite the real high-speed approach before the kick fires.
    if (grid.collideX(this.body, this.vx * dt)) { this.wallHitSpeed = Math.max(this.wallHitSpeed, Math.abs(this.vx)); this.vx = 0; }
    const wasGrounded = this.grounded;
    const res = grid.collideY(this.body, this.vy * dt, false);
    if (res.ceiling) { this.vy = 0; ev.onBump?.(); }
    if (res.grounded) {
      const impactVy = this.vy; // capture BEFORE zeroing — the landing speed for squash juice
      if (this.vy > 0) this.vy = 0;
      this.grounded = true;
      this.timeSinceGroundedMs = 0;
      this.wallHitSpeed = 0; // speed shed on landing (MOMENTUM no longer carries a stale slam)
      this.refillOnGround(c); // DASH-PRECISION: refill dash charges + climb stamina on land
      if (!wasGrounded) ev.onLand?.(impactVy);
    } else {
      this.grounded = false;
      this.timeSinceGroundedMs += dtMs;
    }

    // ── Animation phase + reported state ──────────────────────────────────────
    if (this.grounded && Math.abs(this.vx) > 12 && !this.ducking) {
      this.walkPhase += Math.abs(this.vx) * dt;
    } else {
      this.walkPhase = 0;
    }
    // POLISH_01 #8: fire once on the skid ONSET (false→true) so World can screech + kick dust
    // without machine-gunning the SFX every step of a sustained brake.
    if (this.skidding && !this.prevSkidding) ev.onSkid?.();
    this.prevSkidding = this.skidding;
    this.state = this.computeState(running);
  }

  /** Whether the walk sprite is on the "a" frame (alternates with travel distance). */
  get walkFrameA(): boolean { return Math.floor(this.walkPhase / 55) % 2 === 0; }

  /**
   * Which side a SOLID wall is on for the wall verbs. 0 when grounded, when both verbs
   * are disabled (CLASSIC), or when no adjacent wall. When walls flank BOTH sides (a
   * narrow shaft) the wall the player is pushing INTO wins (default right). Pure
   * positional probe — no state change, integer-only (lint-clean).
   */
  private detectWall(ax: number, groundedNow: boolean, grid: TileGrid): -1 | 0 | 1 {
    if (groundedNow || (!this.wallSlideEnabled && !this.wallJumpEnabled)) return 0;
    const right = grid.solidBeside(this.body, 1);
    const left = grid.solidBeside(this.body, -1);
    if (right && left) return ax < 0 ? -1 : 1;
    if (right) return 1;
    if (left) return -1;
    return 0;
  }

  private computeState(running: boolean): PlayerState {
    if (!this.grounded) return this.vy < 0 ? 'jump' : 'fall';
    if (this.ducking) return 'duck';
    if (this.skidding) return 'skid';
    if (Math.abs(this.vx) > 12) return running && Math.abs(this.vx) > this.config.walkMaxSpeed ? 'run' : 'walk';
    return 'idle';
  }

  // ── COMBAT verbs (player-owned attack hitbox) ─────────────────────────────────
  // The sim half of the one new COMBAT primitive. World owns the overlap PASS + the
  // kill side effect (ctx.killEntity); these methods own arming the hitbox and applying
  // the on-connect feel (bounce / air-refresh / recoil). Integer-only → lint-clean.

  /** Tick the attack/dash step counters down; clear the aim once nothing is live. */
  private tickCombat(): void {
    if (this.attackActiveSteps > 0) this.attackActiveSteps--;
    if (this.attackCooldownSteps > 0) this.attackCooldownSteps--;
    // Hold the dash window while the DASH-FREEZE hitstop is active, so the lunge runs its
    // full length AFTER the freeze (freeze never sets dashFreezeCounter for COMBAT → no-op there).
    if (this.dashActiveSteps > 0 && this.dashFreezeCounter === 0) this.dashActiveSteps--;
    if (this.dashCooldownSteps > 0) this.dashCooldownSteps--;
    if (this.attackActiveSteps === 0 && this.dashActiveSteps === 0) this.attackDir = null;
  }

  /**
   * Arm a strike on this step's rising edge. `attack` (gated on attackEnabled + cooldown
   * + no live swing): aim DOWN when ducking AND airborne (the pogo gesture), else
   * horizontal by facing ('up' is reserved for a later option). `dash` (gated on
   * dashEnabled + cooldown + not already dashing): launch in the input axis, else facing.
   */
  private handleCombatInput(input: InputSnapshot, groundedNow: boolean, ev: PlayerEvents): void {
    const c = this.config;
    if (this.attackEnabled && input.attack && this.attackCooldownSteps === 0 && this.attackActiveSteps === 0) {
      this.attackDir = (input.duck && !groundedNow) ? 'down' : (this.facing > 0 ? 'right' : 'left');
      this.attackActiveSteps = c.attackHitboxActiveSteps;
      this.attackCooldownSteps = c.attackCooldownSteps;
    }
    if (this.dashEnabled && input.dash && this.dashActiveSteps === 0) {
      // Two gating models from one verb: COMBAT = COOLDOWN (dashMaxCharges 0), DASH-PRECISION =
      // CHARGE (dashMaxCharges > 0 → consume a charge, refill on land). COMBAT's branch is the
      // exact prior behaviour (horizontal lunge) so it stays byte-identical.
      const chargeModel = c.dashMaxCharges > 0;
      const canDash = chargeModel ? this.dashCharges > 0 : this.dashCooldownSteps === 0;
      if (canDash) {
        if (chargeModel) {
          this.armEightWayDash(input, c); // 8-way (axisX + aimUp/duck), normalized, pure traversal
          this.dashCharges--;
        } else {
          this.dashDir = input.axisX !== 0 ? (input.axisX > 0 ? 1 : -1) : this.facing;
          this.dashVx = this.dashDir * c.dashVelocity;
          this.dashVy = 0;
          this.attackDir = this.dashDir > 0 ? 'right' : 'left';
          this.dashCooldownSteps = c.dashCooldownSteps;
        }
        this.dashActiveSteps = c.dashSteps;
        ev.onDash?.(this.dashDir); // POLISH_01 #4 — cosmetic dash kick (World: whoosh + trauma + dust)
      }
    }
  }

  /**
   * Arm the 8-way DASH-PRECISION dash from the directional intent at the press: horizontal from
   * axisX, UP from aimUp, DOWN from duck (no aim → the facing direction). Normalized so a
   * diagonal dash is the SAME SPEED as a cardinal one. Math.sqrt is lint-allowed; integer intent
   * in → exact vector out, so a recorded dash reproduces. The dash is pure TRAVERSAL — its kill
   * is gated off in World (offense:false), so the attackDir set here never connects.
   */
  private armEightWayDash(input: InputSnapshot, c: PhysicsConfig): void {
    let ux = input.axisX;                              // -1 | 0 | 1
    let uy = input.aimUp ? -1 : (input.duck ? 1 : 0);  // up beats down; +y is DOWN (screen convention)
    if (ux === 0 && uy === 0) { ux = this.facing; uy = 0; } // no aim → dash forward (facing)
    const len = Math.sqrt(ux * ux + uy * uy);
    this.dashVx = (ux / len) * c.dashVelocity;
    this.dashVy = (uy / len) * c.dashVelocity;
    this.dashDir = ux >= 0 ? 1 : -1;                   // horizontal sign for facing/render streak
    this.attackDir = this.dashDir > 0 ? 'right' : 'left';
    // DASH-FREEZE: the charge dash (DASH-PRECISION) punches in with a brief hitstop before the
    // lunge integrates. COMBAT's cooldown dash never calls this method → no freeze (byte-identical).
    this.dashFreezeCounter = c.dashFreezeSteps;
  }

  /**
   * DASH-PRECISION charge/stamina refresh while grounded: refill air-dash charges to max and top
   * climb stamina back up. No-op for archetypes without the charge model / climb verb
   * (dashMaxCharges 0 → sets 0 over 0; climbEnabled false → stamina untouched) → byte-identical.
   */
  private refillOnGround(c: PhysicsConfig): void {
    if (c.dashMaxCharges > 0) this.dashCharges = c.dashMaxCharges;
    if (this.climbEnabled) this.climbStamina = c.climbStaminaMax;
  }

  /** Restore the air-dash charge + climb stamina mid-air — a dashRefill pickup (DASH-PRECISION).
   *  Called via ctx.player from the Item entity. No-op when the charge model is inactive. */
  refillDash(): void {
    const c = this.config;
    if (c.dashMaxCharges > 0) this.dashCharges = c.dashMaxCharges;
    if (this.climbEnabled) this.climbStamina = c.climbStaminaMax;
  }

  /**
   * The live attack/dash hitbox (an AABB extending past the body in the aim direction),
   * or null when no strike is live. World tests entity overlap against this each step.
   * Pure + integer-only (the reach is a config px). A dash sets attackDir to its
   * horizontal direction, so the same derivation covers sword + dash.
   */
  attackHitbox(): AABB | null {
    if (this.attackActiveSteps === 0 && this.dashActiveSteps === 0) return null;
    if (!this.attackDir) return null;
    const reach = this.config.attackHitboxReach;
    const b = this.body;
    switch (this.attackDir) {
      case 'down':  return { x: b.x, y: b.y + b.h, w: b.w, h: reach };
      case 'up':    return { x: b.x, y: b.y - reach, w: b.w, h: reach };
      case 'left':  return { x: b.x - reach, y: b.y, w: reach, h: b.h };
      case 'right': return { x: b.x + b.w, y: b.y, w: reach, h: b.h };
    }
  }

  /**
   * Apply the COMBAT consequence of a hitbox CONNECT (World calls this once per step when
   * the live hitbox hit ≥1 valid target — a killable enemy or a pogoable hazard):
   *   • a DOWN strike while airborne → automatic pogo bounce (vy = -pogoBounceVelocity,
   *     NO jump press) + refresh air actions so chaining works;
   *   • a dash connect → refresh air + re-arm the dash (cooldown→0) so a kill chains;
   *   • either way → a small knockback recoil OPPOSITE the strike (down-strike up is
   *     covered by the bounce, but applied explicitly so side strikes recoil too).
   */
  onAttackConnect(): void {
    const c = this.config;
    const dir = this.attackDir;
    if (this.dashActiveSteps > 0) {
      this.refreshAir();
      this.dashCooldownSteps = 0; // a dash-kill re-arms the dash → chains
    } else if (dir === 'down' && !this.grounded) {
      this.vy = -c.pogoBounceVelocity; // automatic bounce — the keystone, no jump press
      this.refreshAir();
    }
    const k = c.pogoKnockbackVelocity;
    if (k > 0 && dir) {
      if (dir === 'down') this.vy -= k;       // opposite = up
      else if (dir === 'up') this.vy += k;    // opposite = down
      else if (dir === 'left') this.vx += k;  // opposite = right
      else if (dir === 'right') this.vx -= k; // opposite = left
    }
  }

  /** Re-arm air actions after a pogo/dash connect: reset coyote so a jump fires again,
   *  drop any buffered jump, and clear the dash cooldown so the chain stays alive. */
  private refreshAir(): void {
    this.timeSinceGroundedMs = 0;
    this.jumpBufferTimerMs = 0;
    this.dashCooldownSteps = 0;
  }

  // ── RUN-AND-GUN verb (player-fired projectile) ────────────────────────────────
  // The sim half of the one new RUN-AND-GUN primitive. World/Sim owns the bullet SPAWN
  // (via ev.onShoot → spawnEntityAt); this owns the fire TRIGGER + the 8-way aim. Integer
  // counter + Math.sqrt only → lint-clean + replay-deterministic.

  /** Tick the fire cooldown down (the held-auto-fire rate gate). */
  private tickShoot(): void {
    if (this.shootCooldownSteps > 0) this.shootCooldownSteps--;
  }

  /**
   * Held auto-fire (Isaac, 2026-06-30): while the fire verb is on + FIRE is HELD + the cooldown
   * has elapsed, emit a bullet and re-arm the cooldown (the spray-rate cap). The aim is 8-way,
   * REUSING the dash's resolution (armEightWayDash): horizontal from axisX, UP from aimUp, DOWN
   * from duck; no aim → the facing direction (shoot forward). Normalized so a diagonal bullet is
   * the SAME speed as a cardinal one (Math.sqrt is lint-allowed). No-op unless shootEnabled → every
   * other archetype is byte-identical. `this.facing` is last-step's value (the dash arming reads it
   * the same way), which is the player's current facing.
   */
  private handleShootInput(input: InputSnapshot, ev: PlayerEvents): void {
    if (!this.shootEnabled || !input.fireHeld || this.shootCooldownSteps > 0) return;
    let ux = input.axisX;                              // -1 | 0 | 1
    // Vertical aim (+y is DOWN, screen convention). DOWN (duck) beats UP so a held jump key can't
    // veto an aim-down. And UP counts only while GROUNDED or RISING (vy<0): the jump keys double as
    // aim-up, so if a held jump key always meant "up" you could never fire STRAIGHT or DOWN mid-air
    // (playtest, 2026-07-01). Now: up/up-diagonal on the way UP, then straight/down on the way DOWN.
    let uy = input.duck ? 1 : (input.aimUp && (this.grounded || this.vy < 0) ? -1 : 0);
    if (ux === 0 && uy === 0) { ux = this.facing; uy = 0; } // no aim → shoot forward (facing)
    const len = Math.sqrt(ux * ux + uy * uy);
    this.shootCooldownSteps = this.config.shootCooldownSteps;
    ev.onShoot?.(ux / len, uy / len);
  }
}

/** Move `cur` toward `target` by at most `maxDelta`. */
function approach(cur: number, target: number, maxDelta: number): number {
  if (cur < target) return Math.min(cur + maxDelta, target);
  if (cur > target) return Math.max(cur - maxDelta, target);
  return cur;
}
